<script setup lang="ts">
/**
 * Contact route.
 *
 * Deletable example content, but two details are worth keeping.
 *
 * The FAQ is rendered from `faqItems` — the same array that produces the `FAQPage` structured data
 * on this route. `FAQPage` markup describing questions a visitor cannot see is a documented cause
 * of manual spam actions, and rendering from one source is what makes that impossible here.
 *
 * The disclosures are native `<details>`/`<summary>`, which are keyboard-accessible and expose
 * expanded state with no JavaScript and no ARIA of our own. The chevron is rotated with Tailwind's
 * `group-open:` variant — that is, by the element's own open state rather than by a class some
 * click handler remembers to keep in sync.
 */

/**
 * Vue
 */
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Libraries
 */
import { AtSign, Check, MapPin, Plus, Send, Sparkles } from '@lucide/vue'

/**
 * Components
 */
import AppBackdrop from '@/components/ui/AppBackdrop.vue'
import AppBadge from '@/components/ui/AppBadge.vue'
import AppButton from '@/components/ui/AppButton.vue'
import AppCard from '@/components/ui/AppCard.vue'
import AppContainer from '@/components/ui/AppContainer.vue'
import AppSectionHeader from '@/components/ui/AppSectionHeader.vue'

/**
 * Config
 */
import { faqItems } from '@/config/faq'
import { identityConfig } from '@/config/identity'

const { t } = useI18n()

const state = ref<'idle' | 'sending' | 'sent'>('idle')

/** The parts of the location that are set, joined — the config leaves most of them empty. */
const place = [identityConfig.location.city, identityConfig.location.country].filter(Boolean).join(', ')

function submit(): void {
  state.value = 'sending'
  // Replace with a real endpoint — see services/ for the pattern.
  window.setTimeout(() => {
    state.value = 'sent'
  }, 600)
}
</script>

<template>
  <section class="relative overflow-hidden">
    <AppBackdrop />

    <AppContainer class="relative py-20 md:py-24">
      <AppBadge class="enter font-mono uppercase tracking-[0.2em]" style="--enter-step: 0">
        <Sparkles class="h-3.5 w-3.5" aria-hidden="true" />
        {{ t('contact.eyebrow') }}
      </AppBadge>

      <h1 class="mt-6 font-headline text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl">
        {{ t('contact.title') }}
      </h1>

      <p class="enter mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground" style="--enter-step: 1">
        {{ t('contact.lead') }}
      </p>
    </AppContainer>
  </section>

  <AppContainer as="section" aria-labelledby="form-heading" class="pb-16">
    <h2 id="form-heading" class="sr-only">{{ t('contact.form.title') }}</h2>

    <div class="grid gap-6 lg:grid-cols-[3fr_2fr]">
      <AppCard class="reveal">
        <form class="grid gap-5" @submit.prevent="submit">
          <div class="grid gap-2">
            <label for="contact-name" class="text-sm font-medium text-foreground">{{ t('contact.form.name') }}</label>
            <input
              id="contact-name"
              name="name"
              type="text"
              required
              autocomplete="name"
              :placeholder="t('contact.form.namePlaceholder')"
              class="input-control"
            />
          </div>

          <div class="grid gap-2">
            <label for="contact-email" class="text-sm font-medium text-foreground">{{ t('contact.form.email') }}</label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              autocomplete="email"
              :placeholder="t('contact.form.emailPlaceholder')"
              class="input-control"
            />
          </div>

          <div class="grid gap-2">
            <label for="contact-message" class="text-sm font-medium text-foreground">
              {{ t('contact.form.message') }}
            </label>
            <textarea
              id="contact-message"
              name="message"
              rows="5"
              required
              :placeholder="t('contact.form.messagePlaceholder')"
              class="input-control resize-y"
            ></textarea>
          </div>

          <div class="flex flex-wrap items-center gap-4">
            <AppButton type="submit" :disabled="state === 'sending'">
              {{ state === 'sending' ? t('contact.form.sending') : t('contact.form.submit') }}
              <Send class="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
            </AppButton>

            <!--
              role="status" makes this an aria-live region, so the outcome is announced when it
              appears. Without it a screen-reader user gets no feedback that the form did anything.
            -->
            <p v-if="state === 'sent'" role="status" class="inline-flex items-center gap-2 text-sm text-primary">
              <Check class="h-4 w-4" aria-hidden="true" />
              {{ t('contact.form.success') }}
            </p>
          </div>
        </form>
      </AppCard>

      <!--
        The aside repeats nothing: the address and location come from `identityConfig`, which is also
        what the `contactPoint` in the JSON-LD is built from. A hardcoded address here would be the
        second copy that eventually disagrees with the graph.
      -->
      <aside class="reveal grid gap-4 content-start" style="--reveal-step: 1">
        <AppCard v-if="identityConfig.email" :title="t('contact.details.email')" :heading-level="3">
          <template #icon>
            <AtSign class="h-5 w-5" aria-hidden="true" />
          </template>

          <a :href="`mailto:${identityConfig.email}`" class="text-foreground underline-offset-4 hover:underline">
            {{ identityConfig.email }}
          </a>
        </AppCard>

        <AppCard v-if="place" :title="t('contact.details.location')" :heading-level="3">
          <template #icon>
            <MapPin class="h-5 w-5" aria-hidden="true" />
          </template>

          {{ place }}
        </AppCard>

        <p class="px-1 text-sm leading-relaxed text-muted-foreground">{{ t('contact.details.note') }}</p>
      </aside>
    </div>
  </AppContainer>

  <AppContainer as="section" aria-labelledby="faq-heading" class="pb-24">
    <AppSectionHeader id="faq-heading" :eyebrow="t('contact.faq.eyebrow')" :title="t('contact.faq.title')" />

    <!--
      Plain <details>/<summary>, not a <dl>. A definition list cannot legally contain <details>,
      and <summary> is only valid as the first child of <details> — the accessible-disclosure
      semantics come from <details> itself, which already exposes expanded state to assistive
      technology and works with the keyboard without a line of JavaScript.
    -->
    <div class="mt-10 grid max-w-3xl gap-3">
      <details
        v-for="(item, index) in faqItems"
        :key="item.question"
        class="reveal group rounded-2xl border border-border bg-card/50 px-5 backdrop-blur-sm transition open:bg-card/80 hover:border-primary/40"
        :style="{ '--reveal-step': index }"
      >
        <summary
          class="flex cursor-pointer list-none items-center justify-between gap-4 py-4 font-medium text-foreground [&::-webkit-details-marker]:hidden"
        >
          {{ item.question }}
          <Plus class="h-4 w-4 shrink-0 text-primary transition duration-300 group-open:rotate-45" aria-hidden="true" />
        </summary>

        <p class="pb-5 pr-8 text-sm leading-relaxed text-muted-foreground">{{ item.answer }}</p>
      </details>
    </div>
  </AppContainer>
</template>
